import utils from './utils';

export default (function () {
    const DEFAULT_OPTIONS = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
    };

    let dateUtils = {

        /**
         * Generates a locale date string, based on the browser navigator and options.
         *
         * @param {Date} date
         * @param {Object} options
         * @param {string|undefined} locale - default is undefined
         * @returns {string}
         */
        localeDate: (date, options = DEFAULT_OPTIONS, locale = undefined) => {
            if (!utils.isDate(date)) {
                return date;
            }

            return date.toLocaleString(locale, options);
        },

        /**
         * Convert a date string to a Date object.
         *
         * @param {string} date
         * @returns {Date}
         */
        stringToDate: (date) => {
            if (!date) {
                return '';
            }

            if (utils.isDate(date)) {
                return date;
            }

            // The Replace is need for Safari
            const dateString = date.replace(/-/g, '/');

            return new Date(dateString);
        },

        /**
         * Validates if the first date and the second date are the same day.
         *
         * @param {Date} firstDate
         * @param {Date} secondDate
         * @returns {boolean}
         */
        isSameDay: (firstDate, secondDate) => {
            if (!utils.isDate(firstDate) || !utils.isDate(secondDate)) {
                return false;
            }

            return firstDate.getDate() === secondDate.getDate() &&
                firstDate.getMonth() === secondDate.getMonth() &&
                firstDate.getFullYear() === secondDate.getFullYear();
        },

        /**
         * Validates if the first date and the second date are in the same month.
         *
         * @param {Date} firstDate
         * @param {Date} secondDate
         * @returns {boolean}
         */
        isSameMonth: (firstDate, secondDate) => {
            if (!utils.isDate(firstDate) || !utils.isDate(secondDate)) {
                return false;
            }

            return firstDate.getMonth() === secondDate.getMonth() &&
                firstDate.getFullYear() === secondDate.getFullYear();
        },

        /**
         * Validates if the first date and the second date are in the same year.
         *
         * @param {Date} firstDate
         * @param {Date} secondDate
         * @returns {boolean}
         */
        isSameYear: (firstDate, secondDate) => {
            if (!utils.isDate(firstDate) || !utils.isDate(secondDate)) {
                return false;
            }

            return firstDate.getFullYear() === secondDate.getFullYear();
        },

        /**
         * Validates if the date is the same year as the current calendar year.
         *
         * @param {Date} date
         * @returns {boolean}
         */
        isCalendarYear: (date) => {
            if (!utils.isDate(date)) {
                return false;
            }

            const now = new Date();

            return dateUtils.isSameYear(now, date);
        },

        /**
         * Validates if `date` is after the `afterDate`.
         *
         * @param {Date} date - The date to verify
         * @param {Date} afterDate - The date to check against
         *
         * @returns {boolean}
         */
        isAfter: (date, afterDate) => {
            return afterDate.getTime() < date.getTime();
        },

        /**
         * Validates if `date` is before the `beforeDate`.
         *
         * @param {Date} date - The date to verify
         * @param {Date} beforeDate - The date to check against
         *
         * @returns {boolean}
         */
        isBefore: (date, beforeDate) => {
            return date.getTime() < beforeDate.getTime();
        },

        /**
         * Validates whether the date is the same as today's.
         *
         * @param {Date} date
         *
         * @returns {boolean}
         */
        isToday: (date) => {
            return dateUtils.isSameDay(date, new Date());
        },

        /**
         * Gets the difference in milliseconds between two dates.
         *
         * @param {Date} firstDate
         * @param {Date} secondDate
         * @param {boolean} [absolute=false] - Whether to return an absolute difference.
         *
         * @returns {number}
         */
        diff: (firstDate, secondDate, absolute) => {
            let diff = secondDate.getTime() - firstDate.getTime();

            if (absolute) {
                return Math.abs(diff);
            }

            return diff;
        },

        /**
         * Convert the date to a string.
         *
         * @param {Date} date
         * @param {Object} options
         * @param {string} options.day
         * @param {string} options.month
         * @param {string} options.year
         * @param {string} options.hour
         * @param {string} options.minute
         * @param {boolean} options.hour12
         *
         * @returns {string}
         */
        generateDateString: function (date, options) {
            return this.localeDate(date, options, window.navigator.language);
        },

        /**
         * Compares two dates with eachother and returns a new dateString where the month or years can be merged into one.
         *
         * @param {Date} startDate
         * @param {Date} endDate
         * @returns {String|null}
         */
        combineDatesToString: function (startDate, endDate) {
            if (!utils.isDate(startDate) || !utils.isDate(endDate)) {
                return null;
            }

            let startDateOptions = {};
            let endDateOptions = {};

            // Validate if the start and end is on the same day.
            if (dateUtils.isSameDay(startDate, endDate)) {
                startDateOptions = {
                    day: '2-digit',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit',
                };

                endDateOptions = {
                    hour: '2-digit',
                    minute: '2-digit',
                };

                if (!dateUtils.isCalendarYear(startDate)) {
                    startDateOptions.year = DEFAULT_OPTIONS.year;
                }

                return `${ this.generateDateString(startDate, startDateOptions) } - ${ this.generateDateString(endDate, endDateOptions) }`;
            }

            if (dateUtils.isSameMonth(startDate, endDate)) {
                startDateOptions = {
                    day: '2-digit',
                    month: 'long',
                };

                endDateOptions = {
                    day: '2-digit',
                    month: 'long',
                };

                if (!dateUtils.isCalendarYear(startDate)) {
                    startDateOptions.year = DEFAULT_OPTIONS.year;
                }

                if (!dateUtils.isCalendarYear(endDate)) {
                    endDateOptions.year = DEFAULT_OPTIONS.year;
                }

                return `${ this.generateDateString(startDate, startDateOptions) } - ${ this.generateDateString(endDate, endDateOptions) }`;
            }

            // Validate if the start and end is on the same day.
            if (dateUtils.isSameYear(startDate, endDate)) {
                startDateOptions = {
                    day: '2-digit',
                    month: 'long',
                };

                endDateOptions = {
                    day: '2-digit',
                    month: 'long',
                };

                if (!dateUtils.isCalendarYear(startDate)) {
                    startDateOptions.year = DEFAULT_OPTIONS.year;
                }

                if (!dateUtils.isCalendarYear(endDate)) {
                    endDateOptions.year = DEFAULT_OPTIONS.year;
                }

                return `${ this.generateDateString(startDate, startDateOptions) } - ${ this.generateDateString(endDate, endDateOptions) }`;
            }

            startDateOptions = {
                day: '2-digit',
                month: 'long',
            };

            endDateOptions = {
                day: '2-digit',
                month: 'long',
            };

            return `${ this.generateDateString(startDate, startDateOptions) } - ${ this.generateDateString(endDate, endDateOptions) }`;
        },

        /**
         * Format date
         *
         * @param {Date} date - The date.object.
         * @returns {string} - A new Date object
         */
        formatDate: function (date) {
            const year = date.getFullYear();
            const month = utils.addZero(date.getMonth() + 1);
            const day = utils.addZero(date.getDate());

            return `${ year }-${ month }-${ day }`;
        },

        /**
         * Format date
         *
         * @param {Date} date - The date.object.
         * @returns {string} - string format HH:mm
         */
        formatTime: function (date) {
            const hours = utils.addZero(date.getHours());
            const minutes = utils.addZero(date.getMinutes());

            return `${ hours }:${ minutes }`;
        },

        /**
         * Returns the actual date in Year-month-day format.
         *
         * @returns {string}
         */
        getActualDate: function () {
            const date = new Date();

            return this.formatDate(date);
        },

        /**
         * Format and add days to date time
         *
         * @param {Date} date - The date.object.
         * @param {Number} days - days to add.
         * @returns {string} - A new Date object with time
         */

        addDaysToDate: function (date, days) {
            if (days) {
                date.setDate(date.getDate() + days);
            }

            return this.formatDate(date);
        },

        /**
         * Format and add days to date time
         *
         * @param {Date} date - The date.object.
         * @param {Number} days - days to add.
         * @returns {string} - A new Date object with time
         */

        addDaysToDateTime: function (date, days) {
            return `${ this.addDaysToDate(date, days) } 12:00:00`;
        },

        /**
         * Format datestring to yyyy-mm-dd string format.
         *
         * @param {string} dateString
         * @returns {string} - string format yyyy-mm-dd
         */
        formatDateFromString: function (dateString) {
            const date = this.stringToDate(dateString);

            return this.formatDate(date);
        },

        /**
         * Format datestring to time string format.
         *
         * @param {string} dateString
         * @returns {string}
         */
        formatTimeFromString: function (dateString) {
            const date = this.stringToDate(dateString);

            return this.formatTime(date);
        },

        /**
         * Format datestring to dateTime string format.
         *
         * @param {string} dateString
         * @returns {string}
         */
        formatDateTimeFromString: function (dateString) {
            const date = this.stringToDate(dateString);

            const DATE_OPTIONS = {
                day: 'numeric',
                month: 'long',
            };

            if (!dateUtils.isCalendarYear(date)) {
                DATE_OPTIONS.year = DEFAULT_OPTIONS.year;
            }

            const formattedDateString = (dateString) => {
                return dateString.replace(/\./, '');
            };

            return `${ formattedDateString(this.generateDateString(date, DATE_OPTIONS)) } ${ this.formatTime(date) }`;
        },
    };

    return dateUtils;
})();



// WEBPACK FOOTER //
// ./src/helpers/dateUtils.js